var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import Cookies from 'js-cookie';
import { action, computed, makeObservable, observable, toJS } from 'mobx';
import { safeReaction, SingleConcurrentRequestController } from '../../common';
import { cookieToSegmentMap, getIsFromSelfServiceCookie, resolveSegmentFromPath, setIsFromSelfServiceCookie } from '../../utils';
const SELF_SERVICE_MENU_ID_LIST = ['51', '52', '53', '54', '64', '65'];
export default class MenuItemStore {
    constructor(rootStore, props) {
        Object.defineProperty(this, "slug", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: ''
        });
        Object.defineProperty(this, "name", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: ''
        });
        Object.defineProperty(this, "provided", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "customSegment", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "menuItems", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "frontlineMenuItems", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "dealerMenuActionItems", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "dealerMenuItems", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "storeInitPromise", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "rootStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "menuController", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new SingleConcurrentRequestController()
        });
        Object.defineProperty(this, "dealerMenuController", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new SingleConcurrentRequestController()
        });
        Object.defineProperty(this, "setProvided", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (provided) => {
                const shouldUpdate = this.provided && !provided;
                this.provided = provided;
                if (shouldUpdate) {
                    this.updateMenus();
                }
            }
        });
        Object.defineProperty(this, "setMenuFromProps", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (props) => {
                var _a, _b, _c;
                const newMenuItems = toJS(((_a = props.menu) === null || _a === void 0 ? void 0 : _a.items) || props.menuItems);
                if (newMenuItems && JSON.stringify(newMenuItems) !== JSON.stringify(this.menuItems)) {
                    this.menuItems = newMenuItems;
                    if (!this.rootStore.propStateStore.isSsr) {
                        this.updateIsFromSelfServiceCookie();
                    }
                }
                if (((_b = props.menu) === null || _b === void 0 ? void 0 : _b.name) && props.menu.name !== this.name) {
                    this.name = props.menu.name;
                }
                if (((_c = props.menu) === null || _c === void 0 ? void 0 : _c.slug) && props.menu.slug !== this.slug) {
                    this.slug = props.menu.slug;
                }
                const newDealerMenuItems = toJS(props.dealerMenuItems);
                if (props.dealerMenuItems && JSON.stringify(props.dealerMenuItems) !== JSON.stringify(newDealerMenuItems)) {
                    this.dealerMenuItems = newDealerMenuItems;
                }
            }
        });
        Object.defineProperty(this, "fetchMenu", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (url, controller = this.menuController) => {
                let response = null;
                try {
                    response = await controller.get(url);
                }
                catch (_e) {
                }
                if (response && response.items) {
                    return response;
                }
                return { items: null, slug: '' };
            }
        });
        Object.defineProperty(this, "updateMenus", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async () => {
                if (!(this.rootStore.ssoStore.isInitialized || this.rootStore.ssoStore.isInitializationDelayed)) {
                    return;
                }
                await this.updateMenuItems();
                return this.updateDealerMenuItems();
            }
        });
        Object.defineProperty(this, "updateMenuItems", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async () => {
                if (this.provided) {
                    return;
                }
                const { environmentStore } = this.rootStore;
                const mainMenu = environmentStore.app !== 'frontline' ? await this.fetchMenu(this.getMenuUrl) : {};
                this.setMenuItems(mainMenu);
                this.updateIsFromSelfServiceCookie();
            }
        });
        Object.defineProperty(this, "setMenuItems", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (mainMenu) => {
                this.provided = false;
                this.slug = mainMenu.slug || '';
                this.name = mainMenu.name || '';
                this.menuItems = mainMenu.items || [];
            }
        });
        Object.defineProperty(this, "updateDealerMenuItems", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async () => {
                var _a, _b, _c, _d;
                const { ssoStore } = this.rootStore;
                const { app, URLData } = this.rootStore.environmentStore;
                const dealerMenu = ssoStore.authData.isDealer ? await this.fetchMenu(URLData.getDealerMenuUrl(), this.dealerMenuController) : {};
                if (app === 'frontline') {
                    const menuResponse = await this.fetchMenu(this.getFrontlineMenuUrl);
                    if (menuResponse === null) {
                        return;
                    }
                    this.frontlineMenuItems = menuResponse.items;
                }
                this.dealerMenuItems = (_b = (_a = dealerMenu === null || dealerMenu === void 0 ? void 0 : dealerMenu.items) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.children;
                this.dealerMenuActionItems = (_d = (_c = dealerMenu === null || dealerMenu === void 0 ? void 0 : dealerMenu.items) === null || _c === void 0 ? void 0 : _c[1]) === null || _d === void 0 ? void 0 : _d.children;
            }
        });
        Object.defineProperty(this, "setCustomSegment", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (val) => {
                if (!!this.customSegment && !val) {
                    setIsFromSelfServiceCookie(false);
                }
                this.customSegment = val;
            }
        });
        Object.defineProperty(this, "isPublicWebWithSelfServiceMenu", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                const { app } = this.rootStore.environmentStore;
                return ['online-help', 'public'].includes(app) && this.isSelfServiceMenu();
            }
        });
        Object.defineProperty(this, "updateIsFromSelfServiceCookie", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                const { brand } = this.rootStore.environmentStore;
                if (brand === 'telia' && this.menuItems.length && !this.isPublicWebWithSelfServiceMenu()) {
                    setIsFromSelfServiceCookie(false);
                }
            }
        });
        Object.defineProperty(this, "isSelfServiceMenu", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                var _a;
                return SELF_SERVICE_MENU_ID_LIST.includes(`${(_a = this.menuItems[0]) === null || _a === void 0 ? void 0 : _a.menuId}`);
            }
        });
        Object.defineProperty(this, "resolvePublicAppSegment", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (path) => {
                if (this.customSegment) {
                    return this.customSegment;
                }
                const segment = resolveSegmentFromPath(path);
                return ['era', 'ari', 'partnerile'].includes(segment) ? segment : 'ettevottest';
            }
        });
        Object.defineProperty(this, "resolveSegment", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (segment) => {
                return segment || cookieToSegmentMap(Cookies.get('segment'));
            }
        });
        makeObservable(this);
        this.rootStore = rootStore;
        this.setCustomSegment(props.customSegment);
        if (props.menu || props.menuItems || props.dealerMenuItems) {
            this.setMenuFromProps(props);
            this.provided = true;
            this.storeInitPromise = Promise.resolve();
        }
        else if (!rootStore.propStateStore.isSsr) {
            this.storeInitPromise = this.updateMenus();
        }
        const { ssoStore } = rootStore;
        safeReaction(rootStore, () => `${this.getMenuUrl}${ssoStore.isInitialized}${ssoStore.isInitializationDelayed}${ssoStore.authData.isDealer}`, () => this.updateMenus());
    }
    get getFrontlineMenuUrl() {
        const { URLData } = this.rootStore.environmentStore;
        const path = this.rootStore.locationStore.currentPath;
        return URLData.getResolveFrontlineMenuURL({ path });
    }
    get getMenuUrl() {
        const { app, lang, brand, URLData } = this.rootStore.environmentStore;
        const { segment, currentPath } = this.rootStore.locationStore;
        const { authData, isInitialized } = this.rootStore.ssoStore;
        const { forceSegment } = this.rootStore.propStateStore;
        const resolvedSegment = app === 'public' ? this.resolvePublicAppSegment(currentPath) : this.resolveSegment(segment);
        const isLoggedIn = !isInitialized || authData.isLoggedIn;
        const menuParams = {
            app: ['selfservice', 'public'].includes(forceSegment) ? forceSegment : app,
            lang,
            brand,
            segment: resolvedSegment,
            isLoggedIn,
            isFromSelfService: getIsFromSelfServiceCookie(),
        };
        return URLData.getResolveMenuURL(menuParams);
    }
}
__decorate([
    observable
], MenuItemStore.prototype, "slug", void 0);
__decorate([
    observable
], MenuItemStore.prototype, "name", void 0);
__decorate([
    observable
], MenuItemStore.prototype, "provided", void 0);
__decorate([
    observable
], MenuItemStore.prototype, "customSegment", void 0);
__decorate([
    observable.struct
], MenuItemStore.prototype, "menuItems", void 0);
__decorate([
    observable.struct
], MenuItemStore.prototype, "frontlineMenuItems", void 0);
__decorate([
    observable.struct
], MenuItemStore.prototype, "dealerMenuActionItems", void 0);
__decorate([
    observable.struct
], MenuItemStore.prototype, "dealerMenuItems", void 0);
__decorate([
    action
], MenuItemStore.prototype, "setProvided", void 0);
__decorate([
    action
], MenuItemStore.prototype, "setMenuFromProps", void 0);
__decorate([
    action
], MenuItemStore.prototype, "setMenuItems", void 0);
__decorate([
    action
], MenuItemStore.prototype, "updateDealerMenuItems", void 0);
__decorate([
    action
], MenuItemStore.prototype, "setCustomSegment", void 0);
__decorate([
    computed
], MenuItemStore.prototype, "getMenuUrl", null);
//# sourceMappingURL=MenuItemStore.js.map